import { BrandgenCampaignPool, BrandgenCampaignPoolReport, ReportTimeFrames } from "../../../../types/campaignTypes";
import { CampaignTableRow } from "./campaignTableTypes";
import { StatusBadgeVariant } from "../../elements/StatusBadge";
import { getCampaignPlayTypeLabel } from "../../../../constants/campaignPlayTypes";
import { transformRowValue } from "../../../../helpers/campainRowFormatHelpers";

export const DASHBOARD_REPORT_TIMEFRAME = ReportTimeFrames.ALL_TIME;

export type DashboardMetrics = {
  activeCampaignCount: number;
  totalImpressions: string;
  totalClicks: string;
  spendMtd: string;
};

export const getVisibleCampaigns = (campaigns: BrandgenCampaignPool[]): BrandgenCampaignPool[] =>
  campaigns.filter((campaign) => !campaign.archived);

export const getCampaignReport = (
  campaign: BrandgenCampaignPool,
  timeFrame: ReportTimeFrames = DASHBOARD_REPORT_TIMEFRAME,
): BrandgenCampaignPoolReport | null =>
  campaign.campaignPoolReports?.find((report) => report.timeFrame === timeFrame) ?? null;

export const getCampaignStatus = (campaign: BrandgenCampaignPool): { label: string; variant: StatusBadgeVariant } => {
  if (campaign.status === "processing") {
    return { label: "Processing...", variant: "processing" };
  }
  
  if (campaign.archived) {
    return { label: "Archived", variant: "archived" };
  }

  if (!campaign.active) {
    return { label: "Inactive", variant: "inactive" };
  }

  if (campaign.status === "error") {
    return { label: "Error", variant: "error" };
  }

  const startDate = new Date(campaign.start_date).getTime();

  if (Number.isFinite(startDate) && startDate > Date.now()) {
    return { label: "Scheduled", variant: "scheduled" };
  }

  return { label: "Active", variant: "active" };
};

const toNumber = (value: number | string | null | undefined): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

// Pass the value through untouched: transformRowValue needs to see null/"" to tell "no data" from a real 0,
// and every metric type renders "N/A" in that case so the display matches how the value ranks when sorted.
const formatMetric = (value: number | string | null | undefined, type: "CURRENCY" | "PERCENTAGE" | "FLAT"): string =>
  String(transformRowValue(value, true, true, type));

export const computeDashboardMetrics = (campaigns: BrandgenCampaignPool[]): DashboardMetrics => {
  const visibleCampaigns = getVisibleCampaigns(campaigns);

  const totals = visibleCampaigns.reduce(
    (acc, campaign) => {
      const report = getCampaignReport(campaign);

      return {
        impressions: acc.impressions + toNumber(report?.impressions),
        clicks: acc.clicks + toNumber(report?.clicks),
        spend: acc.spend + toNumber(report?.totalSpend),
      };
    },
    { impressions: 0, clicks: 0, spend: 0 },
  );

  return {
    activeCampaignCount: visibleCampaigns.filter((c) => c.active).length,
    totalImpressions: formatMetric(totals.impressions, "FLAT"),
    totalClicks: formatMetric(totals.clicks, "FLAT"),
    spendMtd: formatMetric(totals.spend, "CURRENCY"),
  };
};

export const mapCampaignsToTableRows = (campaigns: BrandgenCampaignPool[]): CampaignTableRow[] =>
  campaigns.map((campaign) => {
    const report = getCampaignReport(campaign);
    const { label, variant } = getCampaignStatus(campaign);

    return {
      id: campaign.brandgenCampaignPoolId,
      name: campaign.name,
      campaignType: getCampaignPlayTypeLabel(campaign.campaignType),
      status: label,
      statusVariant: variant,
      spend: formatMetric(report?.totalSpend, "CURRENCY"),
      impressions: formatMetric(report?.impressions, "FLAT"),
      clicks: formatMetric(report?.clicks, "FLAT"),
      ctr: formatMetric(report?.ctr, "PERCENTAGE"),
      start_date: campaign.start_date ?? "",
      end_date: campaign.end_date ?? "",
      active: Boolean(campaign.active),
      archived: Boolean(campaign.archived),
      processing: campaign.status === "processing",
      spendRaw: toNumber(report?.totalSpend),
      impressionsRaw: toNumber(report?.impressions),
      clicksRaw: toNumber(report?.clicks),
      ctrRaw: toNumber(report?.ctr),
    };
  });
